import Layers from "slotworks/graphics/layers/layers";
import { AbstractControl, Control } from "slotworks/smart-ui/controls/control";
import ControlOptions from "slotworks/smart-ui/options/control-options";
import HeadlessSmartUIGroup from "slotworks/smart-ui/smart-ui-group";
import { HexToRGB, StringToHex } from "slotworks/utils/math/color";
import Timer from "slotworks/utils/timer";
import * as TweenJS from "slotworks/utils/tween";

export default class FlashingSpriteControl extends AbstractControl implements Control {

    private sprites: PIXI.Sprite[] = [];
    private domElements: HTMLElement[] = [];
    private flasher: TweenJS.Tween;
    private timer: number;

    public setup(name: string, layer: Layers, assetName: string, assetNames: string[], smartUIGroup: HeadlessSmartUIGroup) {
        super.setup(name, layer, assetName, assetNames, smartUIGroup);

        const sprite: PIXI.Sprite = layer.getSprite(assetName);
        if (sprite) {
            // There can be an infinite number of sprites to flash
            this.sprites = [sprite];
            for (let i = 2; true; i++) {
                const additionalSprite: PIXI.Sprite = layer.getSprite(`${assetName}_${i}`);
                if (!additionalSprite) {
                    break;
                }
                this.sprites.push(additionalSprite);
            }
        }

        this.domElements = layer.getDOMElements(assetName);

        this.hideSprites();
        this.hideDomElements();
    }

    public updateOptions(options: ControlOptions) {
        if (this.sprites.length > 1 && options.lossLimit.values.length < this.sprites.length) {
            for (let i = 1; i < options.lossLimit.values.length; i++) {
                this.sprites[i].visible = false;
            }
        }

        if (options.flashLossLimit) {
            if (!this.flasher) {
                this.flash();
            }
        } else {
            this.stopFlashing();
        }
    }

    public destroy() {
        this.stopFlashing();
        this.sprites.forEach((sprite) => sprite.destroy());
        this.sprites = null;
    }

    private flash() {
        this.hideSprites();
        this.hideDomElements();

        const value: { alpha: number } =  { alpha: 0 };
        this.flasher = new TweenJS.Tween(value)
            .to({ alpha: 1 }, 100)
            .yoyo(true)
            .repeat(9)
            .onUpdate(() => {
                this.sprites.forEach((sprite: PIXI.Sprite) => sprite.alpha = value.alpha);
                this.domElements.forEach((element: HTMLElement) => {
                    const colorStr: string = element.dataset.color || "0xFF0000";
                    const colorNum: number = StringToHex(colorStr);
                    const color: { r: number, g: number, b: number } = HexToRGB(colorNum);
                    element.style.background = `rgba(${color.r}, ${color.g}, ${color.b}, ${value.alpha})`;
                });
            })
            .onComplete(() => this.stopFlashing())
            .start();
    }

    private hideSprites() {
        this.sprites.forEach((sprite: PIXI.Sprite) => sprite.alpha = 0);
    }

    private hideDomElements() {
        this.domElements.forEach((element: HTMLElement) => element.style.background = `rgba(0, 0, 0, 0)`);
    }

    private stopFlashing() {
        if (this.flasher) {
            this.flasher.stop();
            this.hideSprites();
            this.hideDomElements();
            this.flasher = null;
        }
        if (this.timer) {
            Timer.clearTimeout(this.timer);
            this.timer = null;
        }
    }
}
